import "../../candidate-intelligence/results/styles.css";
import type { CalibrationAnalytics } from "@shared/labs/level-assessment-calibration-analytics";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { rpcClient } from "@/lib/rpc";
import { ActiveModelProvider, useActiveModel } from "../active-model-context";
import { ModelPicker } from "../model-picker";
import { C, DM, UI } from "../results/ui-tokens";
import {
  AgreementSection,
  CoverageSection,
  EvolutionSection,
  MistakesSection,
  SummarySection,
} from "./sections";
import { Card, Muted } from "./ui";

/**
 * Calibration data hub — the client-facing analytical view over a model's
 * calibration data. Complements the operator command center (`/calibration`):
 * this page is for the CHRO/recruiter who owns the model and wants to see, in
 * plain terms, what the model has learned, where it still disagrees with them,
 * and whether calibration is improving it. Per active model.
 */
export default function LevelAssessmentCalibrationData() {
  return (
    <ActiveModelProvider>
      <Hub />
    </ActiveModelProvider>
  );
}

const navLink: React.CSSProperties = {
  ...UI,
  fontSize: 12,
  color: C.ink50,
  textDecoration: "underline",
  textDecorationStyle: "dotted",
  textUnderlineOffset: 3,
};

function Hub() {
  const { modelId, model } = useActiveModel();
  const [data, setData] = useState<CalibrationAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    (async () => {
      try {
        const res = (await rpcClient.labs.levelAssessmentCalibrationAnalytics({
          modelId,
        })) as CalibrationAnalytics;
        if (cancelled) return;
        setData(res);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Erro inesperado");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  const empty = data != null && data.summary.totalReviews === 0;

  return (
    <div className="ci-stripe ci-stripe-page">
      <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 1100 }}>
        {/* Header */}
        <div style={{ marginBottom: 16 }}>
          <Link
            href="/labs/level-assessment"
            style={{
              ...UI,
              fontSize: 12,
              color: C.ink50,
              textDecoration: "none",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            ← Voltar para avaliação
          </Link>
          <h1
            style={{
              ...DM,
              margin: "8px 0 6px",
              fontSize: 28,
              fontWeight: 600,
              color: C.ink,
              letterSpacing: "-0.01em",
            }}
          >
            Dados de calibração
          </h1>
          <p
            style={{
              ...UI,
              margin: 0,
              fontSize: 14,
              color: C.ink50,
              lineHeight: 1.55,
              maxWidth: 760,
            }}
          >
            Tudo o que o modelo{model ? ` “${model.name}”` : ""} aprendeu com as suas revisões: o
            que já está coberto, onde ele ainda discorda de você, e como vem evoluindo.
          </p>
          <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
            <Link href="/labs/level-assessment/calibration" style={navLink}>
              Centro de calibração
            </Link>
            <Link href="/labs/level-assessment" style={navLink}>
              Avaliar um perfil
            </Link>
          </div>
        </div>

        <ModelPicker />

        {error && (
          <Card style={{ background: C.red5, borderColor: C.red20, marginBottom: 16 }}>
            <Muted>Falha ao carregar os dados de calibração: {error}</Muted>
          </Card>
        )}

        {loading && !data && <Muted>Carregando dados de calibração…</Muted>}

        {data && (
          <>
            {empty ? (
              <Card style={{ marginBottom: 28 }}>
                <div
                  style={{ ...DM, fontSize: 16, fontWeight: 600, color: C.ink, marginBottom: 6 }}
                >
                  Ainda não há dados de calibração para este modelo
                </div>
                <Muted>
                  Os dados aparecem aqui conforme você revisa avaliações. Ao final de cada
                  avaliação, confirme ou corrija o nível de cada competência — cada revisão ensina o
                  modelo. A cobertura abaixo mostra quais competências e níveis ainda faltam
                  ensinar.
                </Muted>
                <div style={{ marginTop: 12 }}>
                  <Link href="/labs/level-assessment" style={{ ...navLink, color: C.red }}>
                    Avaliar um perfil →
                  </Link>
                </div>
              </Card>
            ) : (
              <SummarySection a={data} />
            )}

            <CoverageSection a={data} />

            {!empty && (
              <>
                <AgreementSection a={data} />
                <MistakesSection a={data} />
                <EvolutionSection a={data} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
